import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { EditIcon } from "../../../assets/Icons";
import Attachments from "../../modals/Attachments";

const formatWhen = (value) => (value ? new Date(value).toLocaleString() : "—");

const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  const total = Math.max(0, Math.round(Number(seconds)));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  if (mins === 0) return `${secs}s`;
  if (secs === 0) return `${mins} min`;
  return `${mins} min ${secs}s`;
};

const answerLabel = (item) => {
  if (item?.answer) return item.answer;
  const match = item?.options?.find((option) => option.uuid === item?.responseId);
  return match?.option || "N/A";
};

const responseTone = (label) => {
  const value = String(label || "").toLowerCase();
  if (value === "yes") return "yes";
  if (value === "no") return "no";
  return "na";
};

const countAnswers = (answers = []) => {
  const rows = Array.isArray(answers) ? answers : [];
  return {
    total: rows.length,
    yes: rows.filter((item) => responseTone(answerLabel(item)) === "yes").length,
    no: rows.filter((item) => responseTone(answerLabel(item)) === "no").length,
  };
};

const rowKey = (item, index) => item.uniqueKey || item.questionId || `row-${index}`;

const AnswerTable = ({
  answers,
  emptyTitle,
  emptyText,
  canEdit,
  dirty,
  saving,
  editingKey,
  setEditingKey,
  onChangeResponse,
  onSave,
  onOpenAttachments,
}) => {
  const rows = Array.isArray(answers) ? answers : [];
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!editingKey) return undefined;
    const onDocClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setEditingKey(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [editingKey, setEditingKey]);

  if (!rows.length) {
    return (
      <div className="ff-session-detail__empty">
        <strong>{emptyTitle}</strong>
        <p>{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="ff-session-detail__table-wrap">
      {canEdit && dirty ? (
        <div className="ff-session-detail__save-bar">
          <span>Unsaved response changes</span>
          <button type="button" className="ff-session-detail__save-btn" onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      ) : null}

      <table className="ff-session-detail__table">
        <thead>
          <tr>
            <th className="is-index">#</th>
            <th>Question</th>
            <th className="is-center">Response</th>
            <th className="is-center">Selected List Option</th>
            <th className="is-center">Attachments</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item, index) => {
            const key = rowKey(item, index);
            const label = answerLabel(item);
            const tone = responseTone(label);
            const listOption = item.selectedListOptionLabel;
            const hasAttachments = item.attachments?.length > 0;
            const options = Array.isArray(item.options) ? item.options : [];

            return (
              <tr key={key} className={tone === "no" ? "is-flagged" : undefined}>
                <td className="is-index">{index + 1}</td>
                <td>
                  <div className="ff-session-detail__question">
                    <span>{item.question || item.title || ""}</span>
                    {item.comment ? (
                      <small>
                        <em>Comment:</em> {item.comment}
                      </small>
                    ) : null}
                  </div>
                </td>
                <td className="is-center ff-session-detail__response-cell">
                  <span className={`ff-session-detail__pill is-${tone}`}>{label}</span>
                  {canEdit && options.length > 0 ? (
                    <div className="ff-session-detail__edit-wrap">
                      <button
                        type="button"
                        className="ff-session-detail__edit-btn"
                        title="Edit response"
                        onClick={() => setEditingKey(editingKey === key ? null : key)}
                      >
                        <EditIcon />
                      </button>
                      {editingKey === key ? (
                        <div ref={dropdownRef} className="ff-session-detail__edit-menu">
                          {options.map((opt) => (
                            <button
                              key={opt.uuid}
                              type="button"
                              className={opt.uuid === item.responseId ? "is-active" : undefined}
                              onClick={() => {
                                onChangeResponse(key, opt.uuid, opt.option);
                                setEditingKey(null);
                              }}
                            >
                              {opt.option}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </td>
                <td className="is-center">
                  {listOption ? (
                    <span className="ff-session-detail__option">{listOption}</span>
                  ) : (
                    <span className="ff-session-detail__muted">N/A</span>
                  )}
                </td>
                <td className="is-center">
                  {hasAttachments ? (
                    <button
                      type="button"
                      className="ff-session-detail__view-link"
                      onClick={() => onOpenAttachments(item.attachments)}
                    >
                      View
                    </button>
                  ) : (
                    <span className="ff-session-detail__muted">NA</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const FreezerSessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [row, setRow] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [phase, setPhase] = useState("entry");
  const [showAttachments, setShowAttachments] = useState(false);
  const [attachment, setAttachment] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const canEdit = userData?.user?.permissions?.includes("MACHINE.ADD");

  useEffect(() => {
    if (!configData?.config?.API_URL || !userData?.token) return;
    setLoadError(false);
    axios
      .get(`${configData.config.API_URL}/api/freezer-frost/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${userData.token}` },
      })
      .then((response) => {
        const data = response.data.data;
        setRow(data);
        setDirty(false);
        setEditingKey(null);
        const entryCount = Array.isArray(data?.entryData) ? data.entryData.length : 0;
        const exitCount = Array.isArray(data?.exitData) ? data.exitData.length : 0;
        if (!entryCount && exitCount) setPhase("exit");
        else setPhase("entry");
      })
      .catch(() => setLoadError(true));
  }, [sessionId, configData?.config?.API_URL, userData?.token]);

  useEffect(() => {
    setEditingKey(null);
  }, [phase]);

  const entryStats = useMemo(() => countAnswers(row?.entryData), [row]);
  const exitStats = useMemo(() => countAnswers(row?.exitData), [row]);
  const activeStats = phase === "entry" ? entryStats : exitStats;
  const activeAnswers = phase === "entry" ? row?.entryData : row?.exitData;

  const handleChangeResponse = (key, responseId, answer) => {
    setRow((prev) => {
      if (!prev) return prev;
      const field = phase === "entry" ? "entryData" : "exitData";
      const nextRows = (prev[field] || []).map((item, index) =>
        rowKey(item, index) === key ? { ...item, responseId, answer } : item,
      );
      return { ...prev, [field]: nextRows };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!row || !canEdit || saving) return;
    setSaving(true);
    try {
      const field = phase === "entry" ? "entryData" : "exitData";
      const payload = (row[field] || []).map(({ uniqueKey, options, ...rest }) => rest);
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/freezer-frost/session/${sessionId}/answers`,
        { phase, data: payload },
        { headers: { Authorization: `Bearer ${userData.token}` } },
      );
      setRow(data.data);
      setDirty(false);
      setEditingKey(null);
    } catch (error) {
      window.alert(error?.response?.data?.message || "Could not save responses.");
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="sites-v2 ff-session-detail">
        <button type="button" className="ff-session-detail__back" onClick={() => navigate(-1)}>
          <ArrowBackIcon fontSize="inherit" />
          Back
        </button>
        <div className="ff-session-detail__empty is-page">
          <strong>Couldn’t load this session</strong>
          <p>Please go back and try again.</p>
        </div>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="sites-v2 ff-session-detail">
        <div className="ff-session-detail__loading">
          <div className="ff-session-detail__skeleton is-title" />
          <div className="ff-session-detail__skeleton is-block" />
          <div className="ff-session-detail__skeleton is-block" />
        </div>
      </div>
    );
  }

  const exitEmpty =
    row.status === "entry_open"
      ? "Worker is still inside. Exit answers will appear after the Exit QR is scanned."
      : row.nonCompliant || row.status === "closed_non_compliance"
        ? "Session was closed on entry non-compliance — Exit QR was not required."
        : row.cumulativeLimitReached
          ? "Cumulative exposure limit was reached for this AA Alias during this visit."
          : row.exitQrMissed
            ? "Exposure limit was exceeded before exit was completed."
            : "No exit answers were submitted for this session.";

  const exitTimeLabel =
    row.status === "completed" && row.exitTime ? formatWhen(row.exitTime) : "—";

  const statusLabel = row.nonCompliant || row.status === "closed_non_compliance"
    ? "Non-compliance (closed)"
    : row.cumulativeLimitReached && row.status === "completed"
      ? "Completed — Cumulative time exceeded"
      : row.cumulativeLimitReached
        ? "Cumulative limit reached"
        : row.exitQrMissed
          ? "Exceeded exposure limit"
          : row.exposureLimitExceeded && row.status === "completed"
            ? "Exceeded exposure limit"
            : row.status === "entry_open"
              ? "Active inside"
              : row.status === "completed"
                ? "Completed"
                : row.status;

  const facts = [
    { label: "Worker", value: row.userName || row.userUuid || "—" },
    { label: "AA Alias", value: row.aaAlias || "—" },
    { label: "Entry", value: formatWhen(row.entryTime) },
    { label: "Exit", value: exitTimeLabel },
    { label: "Duration", value: formatDuration(row.durationSeconds) },
    { label: "Status", value: statusLabel },
  ];

  return (
    <div className="sites-v2 ff-session-detail">
      <button
        type="button"
        className="ff-session-detail__back"
        onClick={() => navigate(`/ff/info/${row.parentUuid}?tab=response`, { replace: true })}
      >
        <ArrowBackIcon fontSize="inherit" />
        Back to sessions
      </button>

      <section className="ff-session-detail__facts" aria-label="Session details">
        {facts.map((fact) => (
          <div key={fact.label} className="ff-session-detail__fact">
            <span>{fact.label}</span>
            <strong>{fact.value}</strong>
          </div>
        ))}
      </section>

      <section className="ff-session-detail__answers">
        <div className="ff-session-detail__phase-bar">
          <div className="ff-session-detail__phase-tabs" role="tablist" aria-label="Questionnaire phase">
            <button
              type="button"
              role="tab"
              aria-selected={phase === "entry"}
              className={phase === "entry" ? "is-active" : undefined}
              onClick={() => {
                if (dirty && !window.confirm("Discard unsaved changes?")) return;
                setDirty(false);
                setPhase("entry");
              }}
            >
              Entry
              <span>{entryStats.total}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={phase === "exit"}
              className={phase === "exit" ? "is-active" : undefined}
              onClick={() => {
                if (dirty && !window.confirm("Discard unsaved changes?")) return;
                setDirty(false);
                setPhase("exit");
              }}
            >
              Exit
              <span>{exitStats.total}</span>
            </button>
          </div>

          <div className="ff-session-detail__phase-stats">
            <span className="is-yes">
              <CheckCircleOutlineIcon fontSize="inherit" />
              {activeStats.yes} Yes
            </span>
            <span className="is-no">
              <CancelOutlinedIcon fontSize="inherit" />
              {activeStats.no} No
            </span>
          </div>
        </div>

        <AnswerTable
          answers={activeAnswers}
          emptyTitle={phase === "exit" ? "Exit responses unavailable" : "No entry answers"}
          emptyText={phase === "exit" ? exitEmpty : "No entry answers were recorded for this session."}
          canEdit={canEdit}
          dirty={dirty}
          saving={saving}
          editingKey={editingKey}
          setEditingKey={setEditingKey}
          onChangeResponse={handleChangeResponse}
          onSave={handleSave}
          onOpenAttachments={(files) => {
            setAttachment(files);
            setShowAttachments(true);
          }}
        />
      </section>

      <Attachments
        showAttachments={showAttachments}
        setShowAttachments={setShowAttachments}
        attachment={attachment}
      />
    </div>
  );
};

export default FreezerSessionDetail;
